import isEqual from "lodash-es/isEqual";
import React, { PropsWithChildren } from "react";
import { BreadcrumbItems } from "@/components/Breadcrumb/Breadcrumb";
import { ISVG } from "@/icons/interface";

interface Action {
  type: "SET_TITLE" | "GET_TITLE";
  value: Partial<ITitleContext>;
}

export type { Action };

const TitleContext = React.createContext({} as ITitleContext);
const TitleDispatchContext = React.createContext({} as React.Dispatch<Action>);

export interface ITitleContext {
  text: string;
  menu?: string;
  Icon?:
    | React.FC<ISVG>
    | React.FC<React.SVGProps<SVGSVGElement>>
    | React.ReactNode;
  Breadcrumbs: BreadcrumbItems;
}

function titleReducer(state: ITitleContext, action: Action): ITitleContext {
  const appTitle = "TrustLogix";
  const text = action.value?.text || "";
  switch (action.type) {
    case "SET_TITLE": {
      document.title = `${appTitle} ${text.length ? " : " + text : ""}`;
      const next = { ...state, ...action.value };
      // Compared by value, not identity. `PageTitle`'s effect depends on the
      // whole `Breadcrumbs` array, and most callers pass an inline literal — so
      // without this every render of such a page dispatched a "new" title and
      // re-rendered every title consumer. Pages that build a crumb array from
      // fresh objects (an entity switcher's crumb, say) would otherwise have to
      // memoize it just to stay out of a render loop.
      return isEqual(state, next) ? state : next;
    }
    case "GET_TITLE":
      return { ...state };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const TitleProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = React.useReducer(titleReducer, {
    text: "",
    Breadcrumbs: [],
  });

  return (
    <TitleContext.Provider value={state}>
      <TitleDispatchContext.Provider value={dispatch}>
        {children}
      </TitleDispatchContext.Provider>
    </TitleContext.Provider>
  );
};

function useTitleState(): ITitleContext {
  const context = React.useContext(TitleContext);
  if (context === undefined) {
    throw new Error("useTitleState must be used within a TitleProvider");
  }
  return context;
}

function useTitleDispatch(): React.Dispatch<Action> {
  const context = React.useContext(TitleDispatchContext);
  if (context === undefined) {
    throw new Error("useTitleDispatch must be used within a TitleProvider");
  }
  return context;
}

const setTitle = (dispatch: React.Dispatch<Action>, value: ITitleContext) => {
  dispatch({ type: "SET_TITLE", value });
};

export { TitleProvider, useTitleState, useTitleDispatch, setTitle };
